export type AcuityLevel = 'low' | 'medium' | 'high';

export type AcuityReviewStatus = 'pending_review' | 'approved' | 'rejected';

export type AcuityReviewAction = 'approve' | 'reject';

export type DeescalationCriterionName =
  | 'abnormal_vitals'
  | 'ed_hospital_events'
  | 'falls'
  | 'functional_decline'
  | 'medication_changes'
  | 'diagnosis_floor';

export interface DeescalationCriterionResult {
  criterion: DeescalationCriterionName;
  stable: boolean;
  reason: string;
}

export interface EscalationFlag {
  previousLevel: AcuityLevel;
  proposedLevel: AcuityLevel;
  assessedBy: string;
  assessedByName: string;
  assessmentId: string;
  selectedTriggerIds?: string[];
  manualTriggers?: string[];
  comments?: string;
  source?: 'manual_update';
  taskId?: string;
  status: AcuityReviewStatus;
  createdAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  reviewAction?: AcuityReviewAction;
}

export interface DeescalationFlag {
  eligible?: boolean;
  evaluatedAt?: string;
  suggestedTargetLevel?: AcuityLevel;
  criteria?: DeescalationCriterionResult[];
  assessmentId?: string | null;
  assessedBy?: string;
  assessedByName?: string;
  assessmentDate?: string | null;
  taskId?: string | null;
  status?: AcuityReviewStatus;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  manualTriggers?: string[];
  comments?: string;
  source?: 'manual_update';
  previousLevel?: AcuityLevel;
  createdAt?: string;
  reviewAction?: AcuityReviewAction;
}

export enum AcuityTaskType {
  DEESCALATION_REVIEW = 'ACUITY_DEESCALATION_REVIEW',
  ESCALATION_REVIEW = 'ACUITY_ESCALATION_REVIEW',
  APCM_TRANSITION_REVIEW = 'APCM_TRANSITION_REVIEW',
}

export type SystemAction = 'escalate_next_tier' | 'suggest_medium';

export type TriggerApplicability = 'all' | 'medium_high' | 'low_only';

export interface AcuityTrigger {
  id: string;
  label: string;
  systemAction: SystemAction;
  appliesTo: TriggerApplicability;
}

export interface AcuityTriggersConfig {
  triggers: AcuityTrigger[];
}

export interface AcuityManualTriggers {
  escalation?: string[];
  deescalation?: string[];
}

export type ManualAcuityDirection = 'escalation' | 'deescalation';

export interface SaveManualAcuityRequest {
  requestedLevel: AcuityLevel;
  selectedTriggers: string[];
  comments?: string;
}

export interface ManualAcuityUpdateResponse {
  patientId: string;
  assessmentId: string;
  direction: ManualAcuityDirection;
  requestedLevel: AcuityLevel;
  applied: boolean;
  newAcuityLevel?: AcuityLevel;
  pendingApproval?: boolean;
}

/**
 * An acuity level that changed, and whose it is.
 *
 * The patient id is the point. `AcuityService` broadcasts this on a shared subject, so every
 * subscriber in the app receives every change — including a chart that is currently showing a
 * DIFFERENT patient. Without the id a listener cannot tell whose level it just received, and the
 * patient-chart listener used to apply it to whoever was on screen. `computedAcuityLevel` drives
 * triage order, so that is not a cosmetic mix-up.
 */
export interface AcuityLevelChange {
  patientId: string;
  level: AcuityLevel;
}

export interface UtilizationAssessment {
  id?: string;
  patientId: string;
  assessedBy: string;
  assessedByName: string;
  createdAt: string | null;
  selectedTriggerIds: string[];
  computedAcuityLevel: AcuityLevel;
  pendingEscalationReview?: boolean;
  proposedAcuityLevel?: AcuityLevel;
  source?: 'manual_update';
  direction?: ManualAcuityDirection;
  requestedLevel?: AcuityLevel;
  manualTriggers?: string[];
  comments?: string;
  pendingReview?: boolean;
}

export interface CreateUtilizationAssessmentRequest {
  selectedTriggerIds: string[];
}

export interface AssessmentHistoryResponse {
  records: UtilizationAssessment[];
  totalRecords: number;
  page: number;
  pageSize: number;
}

export interface DeescalationReviewRequest {
  action: 'approve' | 'reject';
  notes?: string;
  taskId: string;
}

export interface DeescalationReviewResponse {
  patientId: string;
  action: 'approve' | 'reject';
  newAcuityLevel?: AcuityLevel;
}

export type ApcmTier = 'tier_1' | 'tier_2' | 'tier_3';

export interface ApcmTransitionReviewRequest {
  action: 'approve' | 'reject';
  taskId: string;
  notes?: string;
  apcmTier?: ApcmTier;
}

export interface ApcmTransitionReviewResponse {
  patientId: string;
  action: 'approve' | 'reject';
  apcmEnabled?: boolean;
}

export interface EscalationReviewRequest {
  action: 'approve' | 'reject';
  notes?: string;
  taskId: string;
}

export interface EscalationReviewResponse {
  patientId: string;
  action: 'approve' | 'reject';
  newAcuityLevel?: AcuityLevel;
}

// Dialog data/result interfaces — moved here from acuity-level-update-dialog.component.ts
// so they can be shared with patient-detail-home.component.ts without a component import.
export interface AcuityLevelUpdateDialogData {
  patientId: string;
  patientName: string;
  currentLevel: AcuityLevel;
  requestedLevel: AcuityLevel;
}

export interface AcuityLevelUpdateDialogResult {
  response?: ManualAcuityUpdateResponse;
}
